import {
  addressToText,
  addressToTextSafe
} from '@dadohr/shared/domainTypes/address';
import {
  ContentType,
  LayoutBlockType,
  SubTaskType,
  TaskBlockTemplate
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { CALCULATED_FIELDS, IUser } from '@dadohr/shared/domainTypes/user';
import firebase from 'firebase/app';
import { compact, flatten, get, keyBy } from 'lodash';
import { AnchorField } from '../../../features/Experiences/components/Anchors';
import { UnsafeTFunction } from '../../../i18next';
import { refreshTimestamp } from '../../firebase';
import { toOrdinal } from '../../numbers';
import { formatPossiblyYearlessMoment, Timestamp, toMoment } from '../../time';
import { getCalculatedFields, toFullName } from '../../user';

export type UserInterpolation = {
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  personalEmail: string;
  location: string;
  role: string;
  team: string;
  department: string;
  phoneNumber: string;
  startDate: Timestamp | null;
  terminationDate: Timestamp | null;
  address: string;
  dateOfBirth: Timestamp | null;
  [key: string]: string | number | boolean | Timestamp | null; // for custom fields!
};

// only take custom fields that actually make sense!
// only strings, numbers, dates. User relationships for example shouldn't be in there!
export const getAllUserInterpolationFields = (
  customFields: CustomUserField[],
  t: UnsafeTFunction
): {
  value: string /* keyof UserInterpolation */;
  label: string;
  type: CustomUserField['type'];
}[] => {
  return [
    { value: 'firstName', label: t('First Name'), type: 'string' },
    { value: 'lastName', label: t('Last Name'), type: 'string' },
    { value: 'fullName', label: t('Full Name'), type: 'string' },
    { value: 'email', label: t('Work Email'), type: 'string' },
    { value: 'personalEmail', label: t('Personal Email'), type: 'string' },
    { value: 'location', label: t('Location'), type: 'string' },
    { value: 'role', label: t('Role'), type: 'string' },
    { value: 'team', label: t('Team'), type: 'string' },
    { value: 'department', label: t('Department'), type: 'string' },
    { value: 'phoneNumber', label: t('Phone Number'), type: 'string' },
    { value: 'address', label: t('Address'), type: 'string' },
    { value: 'startDate', label: t('First day at work'), type: 'date' },
    { value: 'terminationDate', label: t('Last day at work'), type: 'date' },
    { value: 'dateOfBirth', label: t('Date of Birth'), type: 'date' },
    ...flatten(
      CALCULATED_FIELDS.map((c) => {
        if (c.type === 'recurringDate') {
          return compact([
            {
              value: `calculatedFields.${c.key}.date`,
              label: `${c.label} ${t('Date')}`,
              type: 'date' as const
            },
            c.exposeCount && {
              value: `calculatedFields.${c.key}.count`,
              label: `${c.label} ${t('Count')}`,
              type: 'number' as const
            }
          ]);
        }
        return [];
      })
    ),
    ...compact(
      customFields.map((v) =>
        v.type === 'relationship' || v.restricted
          ? null
          : {
              value: v.key,
              label: v.label,
              type: v.type === 'smartDate' ? ('date' as const) : v.type
            }
      )
    )
  ];
};

export const userToInterpolation = (
  user: IUser,
  customFields: CustomUserField[],
  referenceDate: Timestamp,
  dateOverrides: {
    [overridePath: string]: Timestamp;
  } = {}
): UserInterpolation => {
  const { calculatedFields } = getCalculatedFields(user, referenceDate);

  const getUserDateValue = (key: keyof IUser) => {
    return dateOverrides[key] || user[key];
  };
  const getRecurringDateValue = (field: string) => {
    const fullKey = `calculatedFields.recurringDate.${field}.current.date`;
    return (
      dateOverrides[fullKey] ||
      calculatedFields.recurringDate?.[field].current?.date ||
      null
    );
  };

  const getCustomFieldDateValue = (key: string) => {
    const fullKey = `customFields.${key}`;
    return dateOverrides[fullKey] || user.customFields[key] || null;
  };

  const ui: UserInterpolation = {
    firstName: user.preferredName,
    lastName: user.lastName,
    fullName: toFullName(user),
    email: user.email,
    personalEmail: user.personalEmail,
    role: user.role,
    team: user.team,
    department: user.department,
    phoneNumber: user.phoneNumber,
    location: user.location,
    startDate: getUserDateValue('hireDate'),
    terminationDate: getUserDateValue('terminationDate'),
    dateOfBirth: getUserDateValue('dateOfBirth'),
    address: user.address ? addressToText(user.address) : ''
  };

  CALCULATED_FIELDS.forEach((c) => {
    if (c.type === 'recurringDate') {
      ui[`calculatedFields.${c.key}.date`] = getRecurringDateValue(c.field);
      if (c.exposeCount) {
        ui[`calculatedFields.${c.key}.count`] =
          calculatedFields.recurringDate?.[c.field]?.current?.count ?? '';
      }
    }
  });

  customFields.forEach((f) => {
    ui[f.key] =
      f.type === 'address'
        ? addressToTextSafe(user.customFields[f.key] as any)
        : f.type === 'date' || f.type === 'smartDate'
        ? getCustomFieldDateValue(f.key)
        : (user.customFields[f.key] as any);
  });

  return ui;
};

export const getUserFieldForAnchor = (
  anchorFields: AnchorField[],
  anchorFiled: string
) => {
  return anchorFields.find((a) => a.field === anchorFiled)?.key;
};

export type InterpolationVariable = {
  key: string;
  label: string;
  vars: UserInterpolation;
};

export type SubTaskInterpolation =
  | {
      type: SubTaskType.QUESTION;
      completed: 'Yes' | 'No';
      answer: string;
    }
  | {
      type: SubTaskType.SUBMIT;
      completed: 'Yes' | 'No';
      url: string;
    }
  | {
      type: Exclude<
        SubTaskType,
        SubTaskType.QUESTION | SubTaskType.SUBMIT
      >;
      completed: 'Yes' | 'No';
    };

export type ExperienceReplacements = {
  experience: {
    vars: {
      title: string;
      // this is where automations data will live too.
      // seed this object with sane default for missing answers
      tasks: {
        [taskTplId: string]: {
          url: string;
          subTasks: {
            [subTaskTplId: string]: SubTaskInterpolation;
          };
        };
      };
    };
  };
  org: { vars: { name: string } };
  roles: InterpolationVariable[];
};

const interpolateTimestamp = (
  value: Timestamp,
  opts: {
    format: string;
    modifier: string;
    handdlePossibleMissingYear?: boolean;
  }
) => {
  const m = toMoment(value);
  if (opts.modifier === 'mondayOfWeek') {
    m.startOf('isoWeek'); // always the monday - start of week can be Monday or Sunday!
  }
  // ... add others later
  return opts.handdlePossibleMissingYear
    ? formatPossiblyYearlessMoment(m, opts.format)
    : m.format(opts.format);
};

export type InterpolationDefaultOptions = {
  dateFormat: string;
};

// Warn at most once per unknown interpolator key. Admin/template previews
// legitimately contain personalization tokens with no data to resolve (e.g.
// {{participant.firstName}} on a template-level timeline, which passes
// `roles: []`), so the graceful fallback below would otherwise flood the
// console on every render. Still surfaces genuine typos — just once.
const warnedUnknownInterpolators = new Set<string>();
const warnUnknownInterpolatorOnce = (key: string): void => {
  if (warnedUnknownInterpolators.has(key)) return;
  warnedUnknownInterpolators.add(key);
  console.warn(`Unknown interpolator ${key}`);
};

// TODO - does this need to use the experience's timezone?
// The backend does this. Not sure about here.
export const interpolateExperienceText = (
  t: string,
  replacements: ExperienceReplacements,
  defaultOptions: InterpolationDefaultOptions
) => {
  const byKey = keyBy(replacements.roles, (v) => v.key);
  return t.replace(/{{(.*?)}}/g, (m, m1: string) => {
    const [x, defaults] = m1.trim().split(' || ');
    const [s, fullOpts = ''] = x.trim().split(' | ');
    const [opts, optsModifier = ''] = fullOpts.split(' :: ');
    const [key, ...otherFields] = s.split('.');
    const field = otherFields.join('.');
    const interpolator =
      key === 'experience'
        ? replacements.experience
        : key === 'org'
        ? replacements.org
        : byKey[key];
    if (!interpolator) {
      warnUnknownInterpolatorOnce(key);
      return m;
    }
    const value = get(interpolator.vars, field);

    if ((value === null || value === undefined || value === '') && defaults) {
      // We might support several defaults and defaults that lead to other variables later
      // on as well. But not for now. Keeping it simple.
      return defaults.match(/"(.*)"/)?.[1] || '';
    }

    const refreshed = refreshTimestamp(value);
    if (refreshed instanceof firebase.firestore.Timestamp) {
      return interpolateTimestamp(refreshed, {
        format: opts || defaultOptions.dateFormat,
        modifier: optsModifier,
        handdlePossibleMissingYear: [...otherFields].pop() === 'dateOfBirth'
      });
    }
    if (typeof value === 'string') {
      return value || `{{ ${key}.${field} }}`;
    }
    if (typeof value === 'number') {
      return opts === 'ordinal' ? toOrdinal(value) : `${value}`;
    }
    console.warn('Unknown value type', { field, value });
    return m;
  });
};

const getDefaultSalutation = (actorKey: string) => {
  return `Hi {{${actorKey}.firstName}}!`;
};

const FALLBACK_NEUTRAL_SALUTATION = 'Hi!';

export const getSalutation = (
  actorKey: string,
  customSalutations: { [actorKey: string]: string },
  customFallbackSalutations: { [actorKey: string]: string },
  replacements: ExperienceReplacements,
  interpolationOptions: InterpolationDefaultOptions
) => {
  const salutationTemplateText =
    customSalutations[actorKey] || getDefaultSalutation(actorKey);

  const salutation = interpolateExperienceText(
    salutationTemplateText,
    replacements,
    interpolationOptions
  );

  const variablesRegex = /{{(.+?)}}/;

  const containsUnresolvedValiables = variablesRegex.test(salutation);

  if (!containsUnresolvedValiables) {
    return salutation;
  } else {
    const customFallbackSalutation = customFallbackSalutations[actorKey];
    return customFallbackSalutation || FALLBACK_NEUTRAL_SALUTATION;
  }
};

export const interpolate = (t: string, vars: object) => {
  return t.replace(/{{(.+?)}}/g, (m, m1: string) => {
    const [key, opts] = m1
      .trim()
      .split(' | ')
      .map((s) => s.trim());
    const value = get(vars, key);
    if (value === undefined) {
      warnUnknownInterpolatorOnce(key);
      return m;
    }
    const refreshed = refreshTimestamp(value);
    if (refreshed instanceof firebase.firestore.Timestamp) {
      return toMoment(refreshed).format(opts || 'YYYY/MM/DD');
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number') {
      return `${value}`;
    }
    console.warn('Unknown value type', { key, value });
    return m;
  });
};

export type ExperienceInterpolationAnalysis = {
  experience: boolean;
  org: boolean;
  actorVariables: {
    [actorKey: string]: Set<string>; // variables
  };
};

export const analyzeExperienceInterpolation = (
  t: string
): ExperienceInterpolationAnalysis => {
  const r: {
    experience: boolean;
    org: boolean;
    actorVariables: {
      [actorKey: string]: Set<string>; // variables
    };
  } = {
    experience: false,
    org: false,
    // we could simplify this a bit and store the variables underneath each actor,
    // then you don't have to split anymore later
    actorVariables: {}
  };
  // cannot use match, as using match with g and iterating through results will not
  // return match groups
  t.replace(/{{(.+?)}}/g, (m, m1: string) => {
    const [s] = m1.trim().split(' | ');
    const [key, ...variablePath] = s.split('.').map((x) => x.trim());
    const variable = variablePath.join('.');
    const container = (r.actorVariables[key] =
      r.actorVariables[key] || new Set());
    container.add(variable);
    if (key === 'experience') {
      r.experience = true;
    } else if (key === 'org') {
      r.org = true;
    } else {
      const container = (r.actorVariables[key] =
        r.actorVariables[key] || new Set());
      container.add(variable);
    }
    return m;
  });
  return r;
};

const analyzeExperienceInterpolationInBlock = (
  block: TaskBlockTemplate
): ExperienceInterpolationAnalysis => {
  if (block.type === ContentType.TEXT) {
    return analyzeExperienceInterpolation(block.data.t.t);
  }
  if (block.type === ContentType.HEADING) {
    return analyzeExperienceInterpolation(block.data.t);
  }

  if (block.type === ContentType.CALLOUT) {
    return analyzeExperienceInterpolation(block.data.t.t);
  }

  if (block.type === ContentType.DIVIDER) {
    return { experience: false, org: false, actorVariables: {} };
  }
  if (block.type === ContentType.IMAGE) {
    return { experience: false, org: false, actorVariables: {} };
  }

  if (block.type === LayoutBlockType.COLUMNS) {
    return { experience: false, org: false, actorVariables: {} };
  }

  if (block.type === SubTaskType.CONFIRM) {
    return analyzeExperienceInterpolation(block.data.label);
  }
  if (block.type === SubTaskType.DOCUMENT_READ) {
    return analyzeExperienceInterpolation(block.data.title);
  }
  if (block.type === SubTaskType.QUESTION) {
    return analyzeExperienceInterpolation(block.data.question.label);
  }
  if (block.type === SubTaskType.SUBMIT) {
    return analyzeExperienceInterpolation(block.data.label);
  }
  if (block.type === SubTaskType.VISIT) {
    return analyzeExperienceInterpolation(block.data.label);
  }
  if (block.type === SubTaskType.WATCH) {
    return analyzeExperienceInterpolation(block.data.label);
  }
  if (block.type === SubTaskType.CREATE_MEETING) {
    return analyzeExperienceInterpolation(block.data.title);
  }
  if (block.type === SubTaskType.DOCUSIGN_ENVELOPE_SIGN) {
    return { experience: false, org: false, actorVariables: {} };
  }
  if (block.type === SubTaskType.COMPLETE_LMS_MODULE) {
    return analyzeExperienceInterpolation(block.data.description.t);
  }
  console.log(`Unsupported type - ${block.type}`);
  return { experience: false, org: false, actorVariables: {} };
};

export const mergeInterpolationAnalysisResults = (
  a: ExperienceInterpolationAnalysis,
  b: ExperienceInterpolationAnalysis
): ExperienceInterpolationAnalysis => {
  const m: ExperienceInterpolationAnalysis = {
    experience: false,
    org: false,
    actorVariables: {}
  };

  m.experience = m.experience || a.experience;
  m.org = m.org || a.org;
  Object.entries(a.actorVariables).forEach(([k, v]) => {
    const container = (m.actorVariables[k] = m.actorVariables[k] || new Set());
    [...v].forEach((x) => container.add(x));
  });

  m.experience = m.experience || b.experience;
  m.org = m.org || b.org;
  Object.entries(b.actorVariables).forEach(([k, v]) => {
    const container = (m.actorVariables[k] = m.actorVariables[k] || new Set());
    [...v].forEach((x) => container.add(x));
  });
  return m;
};

export const analyzeExperienceInterpolationsInBlocks = (
  blocks: TaskBlockTemplate[]
): ExperienceInterpolationAnalysis => {
  return blocks.reduce<ExperienceInterpolationAnalysis>(
    (m, b) => {
      return mergeInterpolationAnalysisResults(
        m,
        analyzeExperienceInterpolationInBlock(b)
      );
    },
    {
      experience: false,
      org: false,
      actorVariables: {}
    }
  );
};
